/// <reference types="@types/googlemaps" />

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  NgForm,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {Country, EventService, Region, SiteConnector, User} from '@spartacus/core';
import {isNil} from 'lodash-es';
import {Observable, Subscription} from 'rxjs';
import {first, skip, switchMap, take} from 'rxjs/operators';
import {GoogleMapsService} from '@app/product/services/google-maps.service';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {
  DcpCustomerRegistrationAddressModel,
  DcpCustomerRegistrationDecoratorService
} from '@app/services/dcp-customer-registration-decorator.service';
import {
  GoogleAutocomplete,
  GoogleAutocompletePlaceType,
  GoogleAutocompleteResult,
  GoogleAutocompleteService
} from '@app/services/google-autocomplete.service';
import {DcpValidators} from '@app/services/dcp-validators';
import {CountryConfig} from '@app/model/dcp-config-service/country-config';
import {FormValidators} from 'ng-design-system';
import {UserAccountChangedEvent, UserAccountFacade} from '@spartacus/user/account/root';
import {Router} from '@angular/router';
import {UserRegistrationRedirectService} from '@app/services/user-registration/user-registration-redirect.service';
import {UserNameValidationService} from '@app/services/user-registration/user-name-validation.service';
import {UserRegistrationConfigService} from '@app/services/user-registration/user-registration-config.service';
import {DOCUMENT} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CompanyType} from '@app/model/user/dcp-user';
import UDExButton from '@udex/web-components/dist/Button';
import UDExTooltip from '@udex/web-components/dist/Tooltip';
import {B2bRegistrationService} from '../services/b2b-registration.service';
import UDExSelectBox from '@udex/web-components/dist/SelectBox';

@UntilDestroy()
@Component({
  selector: 'dcp-b2i-registration',
  templateUrl: './b2i-registration.component.html',
  styleUrls: ['./b2i-registration.component.scss'],
  viewProviders: [{
    provide: ControlContainer,
    useExisting: NgForm
  }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class B2iRegistrationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('streetAddress', {read: ElementRef, static: false})
  readonly streetAddressRef: ElementRef;

  @ViewChild('UDExAddressTooltipOpener')
  UDExAddressTooltipOpener: ElementRef<UDExButton>;

  @ViewChild('UDExAddressTooltip')
  UDExAddressTooltip: ElementRef<UDExTooltip>;

  @ViewChild('regionCode', {read: ElementRef, static: false}) set element(element: ElementRef) {
    if (element) {
      this.regionCode = element;
    }
  }

  regionCode: ElementRef<UDExSelectBox>;

  readonly currentCountryIso: string = this.countrySelectorService.getCurrentCountry().isocode;
  readonly countries$: Observable<Country[]> = this.siteConnector.getCountries();
  readonly subscription: Subscription = new Subscription();
  addressAutocompleteRef: GoogleAutocomplete;
  regions: Region[] = [];
  isDone: boolean;
  errorStatus: number = null;
  errorMessages: any;
  selectedCountryConfig: string;
  fetchingRegions = true;
  _inProgress = false;

  set inProgress(inProgress: boolean) {
    this._inProgress = inProgress;
    const method = inProgress ? 'disable' : 'enable';
    this.form.get('street')[method]();
    this.form.get('city')[method]();
    this.form.get('addressLine2')[method]();
    this.form.get('postalCode')[method]();
    this.form.get('countryCode')[method]();
    if (!inProgress && !this.fetchingRegions) {
      this.form.get('regionCode')?.enable();
    } else {
      this.form.get('regionCode')?.disable();
    }
    this.changeDetectorRef.detectChanges();
  }

  get inProgress() {
    return this._inProgress;
  }

  form: UntypedFormGroup = this.formBuilder.group({
    street: this.formBuilder.control(
      {value: '', disabled: false},
      {
        validators: [
          Validators.required,
          Validators.maxLength(60),
          DcpValidators.onlyLatin1Characters(),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    city: this.formBuilder.control(
      {value: '', disabled: false},
      {
        validators: [
          Validators.required,
          Validators.maxLength(40),
          DcpValidators.onlyLatin1Characters(),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    addressLine2: this.formBuilder.control(
      {value: '', disabled: false},
      {
        validators: [
          Validators.maxLength(40),
          DcpValidators.onlyLatin1Characters(true),
          DcpValidators.atLeastOneLetter(),
        ]
      }
    ),
    postalCode: this.formBuilder.control(
      {value: '', disabled: false},
      {
        validators: [
          Validators.required,
          Validators.maxLength(10),
          DcpValidators.onlyLatin1Characters()
        ]
      }
    ),
    countryCode: this.formBuilder.control(
      {value: '', disabled: true},
      {validators: Validators.required}
    ),
    regionCode: this.formBuilder.control(
      {value: '', disabled: false},
      {validators: Validators.required}
    ),
  }, {
    validators: [
      DcpValidators.checkMatch('street', 'city'),
      DcpValidators.checkMatch('city', 'addressLine2'),
      DcpValidators.checkMatch('street', 'addressLine2')
    ]
  });

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly siteConnector: SiteConnector,
    private readonly countrySelectorService: CountrySelectorService,
    private readonly dcpConfigService: DcpConfigService,
    private readonly googleAutocompleteService: GoogleAutocompleteService,
    private readonly googleMapService: GoogleMapsService,
    private readonly registrationService: DcpCustomerRegistrationDecoratorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly userAccountFacade: UserAccountFacade,
    private readonly userRegistrationRedirectService: UserRegistrationRedirectService,
    private readonly userRegistrationConfigService: UserRegistrationConfigService,
    private readonly router: Router,
    private readonly eventService: EventService,
    public readonly userNameValidationService: UserNameValidationService,
    private readonly b2bRegistrationService: B2bRegistrationService,
    @Inject(DOCUMENT) public document: Document,
  ) {
  }

  ngOnInit(): void {
    this.subscription.add(
      this.countrySelectorService.countryChangeSubject
        .pipe(untilDestroyed(this))
        .subscribe((isocode: { isoCode: string }) => {
          this.setCountry(isocode.isoCode);
        })
    );
    this.b2bRegistrationService.setErrorMessages({
      street: 'registration.error.noError',
      addressLine2: 'registration.error.noError',
      city: 'registration.error.noError',
      postalCode: 'registration.error.noError',
      countryCode: 'registration.error.noError',
      regionCode: 'registration.error.noError',
    });
    if (this.form) {
      this.b2bRegistrationService.form = this.form;
    }
    this.errorMessages = this.b2bRegistrationService.errorMessages;
  }

  ngOnDestroy(): void {
    this.googleAutocompleteService.unbindAll(this.addressAutocompleteRef);
    this.document.querySelectorAll('div.pac-container').forEach((element) => {
      element.remove();
    });
    this.b2bRegistrationService.untilDestroyed$.next();
    this.b2bRegistrationService.resetErrors();
    this.subscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.googleAutocompleteService.isLoaded().then(() => {
      this.attachGoogleMapsAutocomplete();
    });
    this.setCountry(this.currentCountryIso);
  }

  attachGoogleMapsAutocomplete(): void {
    this.addressAutocompleteRef = this.googleAutocompleteService.bind(
      this.streetAddressRef,
      GoogleAutocompletePlaceType.ADDRESS,
      this.currentCountryIso
    );

    this.googleAutocompleteService.onChanged(
      this.addressAutocompleteRef,
      (result: GoogleAutocompleteResult) => this.onAutocompleteStreet(result)
    );
  }

  setCountry(countryIsoCode: string): void {
    this.form.get('countryCode').patchValue(countryIsoCode);
    this.changeCountry(countryIsoCode);
  }

  changeCountry(countryIsoCode: string): void {
    this.getFormControl('countryCode')?.patchValue(countryIsoCode);
    this.getCountryConfig(countryIsoCode);

    this.googleAutocompleteService.isLoaded().then(() => {
      this.googleAutocompleteService.setCountry(this.addressAutocompleteRef, countryIsoCode);
    });

    this.b2bRegistrationService.resetInputs(['street', 'addressLine2', 'city', 'postalCode', 'regionCode']);
    this.resetRegionCodeSelector(this.regionCode);
    this.getRegions(countryIsoCode);
  }

  onCountryChanged(event: CustomEvent): void {
    const countryIsoCode = event.detail.item._id;
    this.changeCountry(countryIsoCode);
  }

  onInputChange(event: CustomEvent, field: string): void {
    const control = this.getFormControl(field);

    const target = event.target as HTMLInputElement;
    control.setValue(target.value);
    this.b2bRegistrationService.validateAllInputs();
  }

  onRegionCodeChanged(event: Event): void {
    const controlName = 'regionCode';
    this.getFormControl(controlName)?.patchValue((event as CustomEvent).detail.item.dataset.value);
    this.b2bRegistrationService.validateInput(controlName);
  }

  getRegions(countryIsoCode: string): void {
    this.fetchingRegions = true;
    this.siteConnector.getRegions(countryIsoCode)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((regions: Region[]) => {
        const controlName = 'regionCode';
        this.fetchingRegions = false;
        this.getFormControl(controlName)?.reset();
        this.getFormControl(controlName)?.patchValue('');
        this.regions = regions;
        this.b2bRegistrationService.validateInput(controlName);
        this.updateValidators();
        this.changeDetectorRef.detectChanges();
      });
  }

  getCountryConfig(countryIsoCode: string): void {
    if (this.selectedCountryConfig !== countryIsoCode) {
      this.selectedCountryConfig = countryIsoCode;
      this.dcpConfigService.getCountryConfig(countryIsoCode)
        .pipe(take(1), untilDestroyed(this))
        .subscribe((config: CountryConfig) => {
          if (config.postalCodeFormat !== '') {
            this.form.get('postalCode').setValidators([
              Validators.required,
              FormValidators.regExp(config.postalCodeFormat)
            ]);
          } else {
            this.form.get('postalCode').setValidators([
              Validators.required,
              Validators.maxLength(10),
              DcpValidators.onlyLatin1Characters()
            ]);
          }

          this.form.get('postalCode').updateValueAndValidity();
          this.changeDetectorRef.detectChanges();
        }, () => {
          this.form.get('postalCode').updateValueAndValidity();
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  updateValidators(): void {
    if (this.regions.length > 0) {
      this.form.get('regionCode').setValidators([Validators.required]);
    } else {
      this.form.get('regionCode').clearValidators();
    }
    this.form.get('regionCode').updateValueAndValidity();
  }

  isRequired(control: AbstractControl): boolean {
    if (control?.validator) {
      const validator = control.validator({} as AbstractControl);
      return validator && validator.required;
    }
    return false;
  }

  onAutocompleteStreet(result: GoogleAutocompleteResult): void {
    const region: Region = this.googleMapService.getRegion(
      this.regions,
      this.googleMapService.fetchAddressType('administrative_area_level_1', result)
    );

    this.form.get('street').patchValue(
      this.googleMapService.getPlaceStreet(result)
    );
    this.form.get('city').patchValue(
      this.googleMapService.getPlaceTown(result)
    );
    this.form.get('postalCode').patchValue(
      this.googleMapService.getPlacePostalCode(result)
    );
    this.form.get('regionCode').patchValue(
      region?.isocode || ''
    );
    this.changeDetectorRef.detectChanges();
  }

  trackByRegion(index: number, region: Region): string {
    return region?.isocode;
  }

  getFormData(): DcpCustomerRegistrationAddressModel {
    let formData: DcpCustomerRegistrationAddressModel;
    if (this.regions.length > 0) {
      formData = Object.assign(
        {},
        this.form.value,
        {
          regionCode: this.form.value.regionCode?.split('-')?.[1] || null,
        },
        {
          countryCode: this.form.get('countryCode').value
        },
      );
    } else {
      this.form.removeControl('regionCode');
      formData = Object.assign(
        {},
        this.form.value,
        {
          countryCode: this.form.get('countryCode').value
        }
      );
    }
    const username = this.userNameValidationService.getValidUserName();
    formData.firstName = username.firstName;
    formData.lastName = username.lastName;
    return formData;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.b2bRegistrationService.setShowErrorMessages(true);
      this.b2bRegistrationService.validateAllInputs();
      return;
    }
    this.inProgress = true;
    this.reset();
    this.userAccountFacade.get()
      .pipe(
        first(),
        switchMap((user: User) => this.registrationService.updateB2BUnit({
          b2bUnitUid: user?.orgUnit?.uid,
          b2bCustomerUid: user?.uid,
          customerId: user?.customerId,
          addresses: [this.getFormData()],
          group: CompanyType.INDIVIDUAL
        })),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.inProgress = false;
        this.isDone = true;
        this.changeDetectorRef.detectChanges();
        this.userAccountFacade.get().pipe(skip(1), take(1), untilDestroyed(this)).subscribe(() => {
          this.router.navigateByUrl((this.userRegistrationRedirectService.getCurrent() || '/')).then(() => {
            this.userRegistrationRedirectService.resetNavigationUrl();
            this.userRegistrationConfigService.resetConfig();
          });
        });
        this.eventService.dispatch(new UserAccountChangedEvent());
      }, (err: { status: number }) => {
        this.inProgress = false;
        this.errorStatus = err.status;
        this.changeDetectorRef.detectChanges();
      });
  }

  reset(): void {
    this.isDone = false;
    this.errorStatus = null;
  }


  showAddressTooltip() {
    const tooltipNativeElement = this.UDExAddressTooltip.nativeElement as UDExTooltip;
    const opener = this.UDExAddressTooltipOpener.nativeElement as UDExButton;
    tooltipNativeElement.onOpen(opener);
  }

  hideAddressTooltip() {
    const tooltipNativeElement = this.UDExAddressTooltip.nativeElement as UDExTooltip;
    tooltipNativeElement.onClose();
  }

  hasError(): boolean {
    return !isNil(this.errorStatus);
  }

  onCancel(): void {
    this.b2bRegistrationService.onCancel();
  }

  setShowErrorMessages(state: boolean): void {
    this.b2bRegistrationService.setShowErrorMessages(state);
  }

  getErrorStatus(field: string): string {
    return this.b2bRegistrationService.getErrorStatus(field);
  }

  getErrorMessage(field: string): string {
    return this.errorMessages[field];
  }

  getFormControl(field: string): AbstractControl {
    return this.form.get(field);
  }

  getMaxLength(field: string): number {
    return this.b2bRegistrationService.getMaxLength(field);
  }

  isMaxLengthError(field: string): boolean {
    return this.b2bRegistrationService.isMaxLengthError(field);
  }

  shouldShowErrorMessages(): boolean {
    return this.b2bRegistrationService.showErrorMessages;
  }

  resetRegionCodeSelector(elRef: ElementRef): void {
    elRef.nativeElement._singleSelectedOption = '';
  }
}
